import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, Container, Paper, Typography } from '@material-ui/core'
import { Translate } from 'react-localize-redux'
import { makeStyles } from '@material-ui/core/styles'
import * as Sentry from '@sentry/react'
import { CLUB_SUBSCRIPTION_ROUTE } from '../../../constants/routes'
import {
  cancelPayPalSubscription,
  createStripeCancelSession,
  createStripeCheckoutSession,
  createStripeSubscription,
  createStripeUpdateSession,
  renewStripeSubscription,
} from '../api'
import { Snackbars } from '../../../components'
import CallToActionHeader from './CallToActionHeader'
import SubscriptionPlansTable from './SubscriptionPlansTable'
import SubscriptionsList from './SubscriptionsList'

const useStyles = makeStyles((theme) => ({
  card: {
    maxWidth: 420,
    width: '100%',
    alignItems: 'center',
    margin: '40px',
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
}))

const ClubSubscriptionsContainer = ({ club, userCurrency }) => {
  const classes = useStyles()

  const [isLoading, setIsLoading] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const subscriptionPlansWrapperRef = useRef(null)
  const [snackbars, setSnackbars] = useState(false)

  useEffect(() => {
    if (selectedSubscription && subscriptionPlansWrapperRef.current) {
      subscriptionPlansWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [selectedSubscription])

  // check if billing enabled on club and user has such permission
  if (!club.can.includes('manage-billing')) {
    return (
      <Paper
        component={Box}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <Card className={classes.card} variant="outlined">
          <Typography variant="h4" component="h4">
            <Translate id="plans.permission-to-manage-billing" />
          </Typography>
        </Card>
      </Paper>
    )
  }

  // display CTA only when club has not subscriptions
  const showCallToAction = club.subscriptions.length === 0

  // display subscription plans only when the club:
  // - has no main subscription
  // - or has no Stripe main updatable subscription
  // - or current subscription selected
  const withSubscriptionPlans = !!selectedSubscription || club.subscriptions.length === 0 || club.subscriptions.filter(s => {
    // exclude addons
    if (s.type !== 'main') {
      return false
    }

    // exclude manual gateway
    if (s.gateway === 'manual') {
      return false
    }

    // exclude valid in-app purchases
    if (['apple', 'google'].includes(s.gateway)) {
      return s.valid
    }

    // exclude updatable stripe subscriptions
    return s.updatable
  }).length === 0

  const successUrl =
    process.env.REACT_APP_URL +
    CLUB_SUBSCRIPTION_ROUTE.populateURLParams({
      clubUuid: club.uuid,
    })
  const cancelUrl =
    process.env.REACT_APP_URL +
    CLUB_SUBSCRIPTION_ROUTE.populateURLParams({
      clubUuid: club.uuid,
    })

  const handleSubscribe = (planUuid, amount) => {
    setIsLoading(true)
    setSnackbars(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Subscribe button',
      data: {
        amount,
        club_uuid: club.uuid,
        subscription_plan_uuid: planUuid,
      },
    })

    // If the user is trying to subscribe to the free plan,
    // we need to create the subscription directly without a session.
    // Otherwise - create checkout session and redirect user to it.
    if (amount === 0) {
      createStripeSubscription(club.uuid, {
        subscription_plan_uuid: planUuid,
      })
        .then(() => {
          // noinspection JSUnresolvedReference
          window.location.reload()
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    } else {
      createStripeCheckoutSession(club.uuid, {
        subscription_plan_uuid: planUuid,
        success_url: successUrl,
        cancel_url: cancelUrl,
      })
        .then((res) => {
          const { url } = res
          window.location.href = url
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    }
  }

  const handleCancelSubscription = (subscription) => {
    setIsLoading(true)
    setSnackbars(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Cancel button',
      data: {
        club_uuid: club.uuid,
        subscription,
      },
    })

    if (subscription.gateway === 'paypal') {
      cancelPayPalSubscription(club.uuid)
        .then(() => {
          // noinspection JSUnresolvedReference
          window.location.reload()
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    } else {
      createStripeCancelSession(subscription.uuid, {
        success_url: successUrl,
        cancel_url: cancelUrl,
      })
        .then((res) => {
          const { url } = res
          window.location.href = url
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    }
  }

  const handleSubscriptionRenew = (subscription) => {
    setIsLoading(true)
    setSnackbars(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Renew button',
      data: {
        club_uuid: club.uuid,
        subscription,
      },
    })

    renewStripeSubscription(subscription.uuid)
      .then(() => {
        // noinspection JSUnresolvedReference
        window.location.reload()
      })
      .catch((error) => {
        setIsLoading(false)
        setSnackbars({
          message: error.toString(),
          error: true,
        })
        Sentry.captureException(error)
      })
  }

  const handleManagePlanClick = (subscription) => {
    setSelectedSubscription(subscription)

    if (subscriptionPlansWrapperRef.current) {
      subscriptionPlansWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  const handleSubscriptionUpdate = (subscription, planUuid) => {
    setIsLoading(true)
    setSnackbars(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Update button',
      data: {
        club_uuid: club.uuid,
        subscription,
        plan_uuid: planUuid,
      },
    })

    createStripeUpdateSession(subscription.uuid, {
      subscription_plan_uuid: planUuid,
      success_url: successUrl,
      cancel_url: cancelUrl,
    })
      .then((res) => {
        const { url } = res
        window.location.href = url
      })
      .catch((error) => {
        setIsLoading(false)
        setSnackbars({
          message: error.toString(),
          error: true,
        })
        Sentry.captureException(error)
      })
  }

  return (
    <Box
      component={Paper}
      p={3}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      {snackbars && <Snackbars {...snackbars} />}
      {showCallToAction && <CallToActionHeader />}
      <Container fixed className={classes.root}>
        {club.subscriptions.length > 0 && (
          <SubscriptionsList
            isLoading={isLoading}
            subscriptions={club.subscriptions}
            onCancel={handleCancelSubscription}
            onRenew={handleSubscriptionRenew}
            onManagePlan={handleManagePlanClick}
          />
        )}
        {withSubscriptionPlans && (
          <div ref={subscriptionPlansWrapperRef}>
            <SubscriptionPlansTable
              isLoading={isLoading}
              onSubscribe={handleSubscribe}
              onUpdate={handleSubscriptionUpdate}
              subscription={selectedSubscription}
              userCurrency={userCurrency}
            />
          </div>
        )}
      </Container>
    </Box>
  )
}

ClubSubscriptionsContainer.defaultProps = {
  userCurrency: null,
}

ClubSubscriptionsContainer.propTypes = {
  club: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        plan_display_name: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        trial_ends_at: PropTypes.string,
        ends_at: PropTypes.string,
        gateway: PropTypes.string.isRequired,
        cancellable: PropTypes.bool.isRequired,
        renewable: PropTypes.bool.isRequired,
        updatable: PropTypes.bool.isRequired,
        can: PropTypes.arrayOf(PropTypes.string).isRequired,
      })
    ),
    can: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  userCurrency: PropTypes.string,
}

export default ClubSubscriptionsContainer
